import React, { useEffect, useId } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { UploadCloud } from 'lucide-react';

import { theme } from '../../styles/theme';

interface PhotoDropzoneProps {
  title: string;
  description: string;
  disabled?: boolean;
  autoOpen?: boolean;
  onFileSelected: (file: File) => void;
  onError?: (message: string) => void;
}

const DropzoneContainer = styled.div<{ $isDragActive: boolean; $disabled?: boolean }>`
  border: 2px dashed
    ${({ $isDragActive }) => ($isDragActive ? theme.colors.primary[400] : theme.colors.glow.mint)};
  border-radius: ${theme.borderRadius['3xl']};
  padding: 1.75rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? theme.colors.glow.mint : theme.colors.primary[400])};
    box-shadow: ${({ $disabled }) => ($disabled ? 'none' : theme.shadows.base)};
  }

  @media (max-width: 1023px) {
    padding: 0.75rem 0.5rem;
  }
`;

const Title = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: 0.5rem;
  color: ${theme.colors.text.primary};

  @media (max-width: 1023px) {
    font-size: ${theme.typography.fontSize.base};
    margin-bottom: 0.25rem;
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: 1rem;
  line-height: 1.5;

  @media (max-width: 1023px) {
    font-size: ${theme.typography.fontSize.xs};
    margin-bottom: 0.5rem;
  }
`;

const UploadButton = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ $disabled }) => ($disabled ? theme.colors.neutral[300] : theme.colors.primary[500])};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: 0.6rem 1.25rem;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  ${DropzoneContainer}:hover &:not([data-disabled]) {
    background: ${theme.colors.primary[600]};
  }
`;

export const PhotoDropzone: React.FC<PhotoDropzoneProps> = ({
  title,
  description,
  disabled,
  autoOpen,
  onFileSelected,
  onError,
}) => {
  const inputId = useId();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    acceptedFiles,
  } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    disabled,
    multiple: false,
    noKeyboard: true,
    noClick: true,
    onDropAccepted: (files) => {
      if (files.length > 0) {
        onFileSelected(files[0]);
      }
    },
    onDropRejected: (rejections) => {
      if (!onError) return;
      if (rejections.length === 0) return;
      const rejection = rejections[0];
      const message = rejection.errors?.map((err) => err.message).join('. ') || 'Unsupported file';
      onError(message);
    },
  });

  useEffect(() => {
    if (autoOpen && !disabled && acceptedFiles.length === 0) {
      open();
    }
  }, [autoOpen, disabled, open, acceptedFiles.length]);

  const inputProps = getInputProps({ id: inputId });

  return (
    <DropzoneContainer {...getRootProps()} $isDragActive={isDragActive} $disabled={disabled}>
      <input {...inputProps} aria-label={`${title} upload`} name="photoUpload" />
      <label
        htmlFor={inputId}
        style={{
          display: 'block',
          cursor: disabled ? 'not-allowed' : 'pointer',
          margin: 0,
        }}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          e.stopPropagation();
        }}
      >
        <UploadCloud size={36} color={theme.colors.primary[400]} />
        <Title>{title}</Title>
        <Description>{description}</Description>
        <UploadButton $disabled={disabled} data-disabled={disabled ? true : undefined}>
          <UploadCloud size={18} />
          Choose photo
        </UploadButton>
      </label>
    </DropzoneContainer>
  );
};


